import React from "react";
import Link from "next/link";
import { isUUID } from "./unified-field-renderer";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import { SmartTicketDisplay } from "@/components/shared/smart-display/SmartTicketDisplay";
import { SmartCompanyDisplay } from "@/components/shared/smart-display/SmartCompanyDisplay";
import { SmartLinkedEntityDisplay } from "@/components/shared/smart-display/SmartLinkedEntityDisplay";
import type { FieldValue } from "../types/index";

/**
 * True for a plain relation object ({ id?, name?, ... }) — excludes arrays and
 * Date so the caller can safely read display fields off it. Deliberately does
 * NOT require an `id` key: relation objects can arrive without a usable id
 * (e.g. an FTS row synthesized from a denormalized name, or a GraphQL selection
 * that omitted id), and those must still render their name instead of falling
 * through to `String(value)` → "[object Object]".
 */
function isRelationObject(value: FieldValue): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

/**
 * Renders a ticket relation cell (by ID or embedded object).
 */
export function renderTicketCell(
  value: FieldValue,
  fieldId: string,
  isEditing: boolean
): React.ReactNode | null {
  if (fieldId !== "ticket_id" && !fieldId.includes("ticket")) return null;

  if (isRelationObject(value)) {
    const ticketRecord = value;
    return (
      <SmartTicketDisplay
        ticketId={String(ticketRecord.id ?? "")}
        ticketData={{
          id: typeof ticketRecord.id === "string" ? ticketRecord.id : undefined,
          ticket_number:
            typeof ticketRecord.ticket_number === "number" ||
            typeof ticketRecord.ticket_number === "string"
              ? ticketRecord.ticket_number
              : null,
          title: typeof ticketRecord.title === "string" ? ticketRecord.title : null,
        }}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
  }
  if (isUUID(String(value))) {
    return (
      <SmartTicketDisplay
        ticketId={String(value)}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
  }
  return null;
}

/**
 * Renders a user relation cell (by ID or embedded object).
 */
export function renderUserCell(
  value: FieldValue,
  fieldId: string,
  isEditing: boolean
): React.ReactNode | null {
  const isUserField =
    fieldId === "assigned_to" ||
    fieldId === "user_id" ||
    fieldId === "created_by" ||
    fieldId === "updated_by" ||
    fieldId.includes("user") ||
    fieldId.includes("assign");

  if (!isUserField) return null;

  if (isRelationObject(value)) {
    const userRecord = value;
    return (
      <SmartUserDisplay
        userId={String(userRecord.id ?? "")}
        userData={{
          id: typeof userRecord.id === "string" ? userRecord.id : undefined,
          first_name:
            typeof userRecord.first_name === "string" ? userRecord.first_name : null,
          last_name:
            typeof userRecord.last_name === "string" ? userRecord.last_name : null,
          email: typeof userRecord.email === "string" ? userRecord.email : null,
        }}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
  }
  if (isUUID(String(value))) {
    return (
      <SmartUserDisplay
        userId={String(value)}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
  }

  // Unresolved webhook attribution (raw email stored in user_id before claim)
  if (typeof value === "string" && value.includes("@")) {
    return (
      <SmartUserDisplay
        userId={value}
        fallbackText={value}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
  }

  return null;
}

/**
 * Wraps contact display in a link to the contact page.
 * On card surfaces or while editing, returns content unwrapped.
 */
export function wrapContactLink(
  contactId: string,
  content: React.ReactNode,
  isEditing: boolean,
  cardSurface: boolean
): React.ReactNode {
  if (isEditing || !contactId || cardSurface) return content;
  return (
    <Link
      href={`/contacts/${contactId}`}
      className="inline-block hover:text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
      onClick={(e) => e.stopPropagation()}
    >
      {content}
    </Link>
  );
}

/**
 * Renders a contact relation cell (contact_id or customer_id, by ID or embedded object).
 */
export function renderContactCell(
  value: FieldValue,
  fieldId: string,
  isEditing: boolean,
  cardSurface: boolean
): React.ReactNode | null {
  const isContactField =
    fieldId === "contact_id" ||
    fieldId.includes("contact") ||
    fieldId === "customer_id" ||
    fieldId.includes("customer");

  if (!isContactField) return null;

  if (isRelationObject(value)) {
    const contactData = value;
    const contactId = typeof contactData.id === "string" ? contactData.id : "";
    const display = (
      <SmartContactDisplay
        contactId={contactId}
        contactData={contactData}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
    return wrapContactLink(contactId, display, isEditing, cardSurface);
  }
  if (isUUID(String(value))) {
    const contactId = String(value);
    const display = (
      <SmartContactDisplay
        contactId={contactId}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
    return wrapContactLink(contactId, display, isEditing, cardSurface);
  }
  return null;
}

/**
 * Renders a deal relation cell (e.g. `contracts.deal_id`), by ID or embedded
 * object. Resolves the deal's title + `/deals/{id}` link via the generic
 * linked-entity display so a deal FK stops rendering as a raw UUID — parity
 * with the company/contact relation cells. Scoped to `deal_id` (and
 * `*_deal_id`) so it never captures unrelated fields.
 */
export function renderDealCell(
  value: FieldValue,
  fieldId: string,
  isEditing: boolean
): React.ReactNode | null {
  const isDealField = fieldId === "deal_id" || fieldId.endsWith("_deal_id");
  if (!isDealField) return null;

  if (isRelationObject(value)) {
    const dealData = value;
    const dealId = typeof dealData.id === "string" ? dealData.id : "";
    // Prefer the embedded title when the query hydrated the relation object.
    const title = typeof dealData.title === "string" ? dealData.title.trim() : "";
    if (dealId && title) {
      return isEditing ? (
        <span className="inline-block">{title}</span>
      ) : (
        <Link
          href={`/deals/${dealId}`}
          className="inline-block hover:text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
          onClick={(e) => e.stopPropagation()}
        >
          {title}
        </Link>
      );
    }
    if (dealId) {
      return (
        <SmartLinkedEntityDisplay entityType="deal" entityId={dealId} disabled={isEditing} />
      );
    }
    return null;
  }
  if (isUUID(String(value))) {
    return (
      <SmartLinkedEntityDisplay entityType="deal" entityId={String(value)} disabled={isEditing} />
    );
  }
  return null;
}

/**
 * Renders a company relation cell (by ID or embedded object).
 */
export function renderCompanyCell(
  value: FieldValue,
  fieldId: string,
  isEditing: boolean
): React.ReactNode | null {
  if (fieldId !== "company_id" && !fieldId.includes("company")) return null;
  // company_name is a plain string display field — skip relation rendering
  if (fieldId === "company_name") return String(value);

  if (isRelationObject(value)) {
    const companyData = value;
    return (
      <SmartCompanyDisplay
        companyId={typeof companyData.id === "string" ? companyData.id : ""}
        companyData={companyData}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
  }
  if (isUUID(String(value))) {
    return (
      <SmartCompanyDisplay
        companyId={String(value)}
        showIcon={true}
        className="inline-block"
        disabled={isEditing}
        showPopover={false}
      />
    );
  }
  return null;
}
